import { useCallback, useEffect, useRef, useState } from "react";
import { formatKstStamp } from "@rma-orbit/shared";
import type { CrawlStatus } from "@rma-orbit/shared";

import { api } from "../api/client.js";
import { agentGet, agentPost, type AgentState } from "../lib/agent.js";
import { Busy, ErrorNotice, Notice, useAsync } from "../lib/ui.js";

/**
 * 수집 화면 (harness/CRAWL-POLICY.md §1, UI-SPEC §1-2).
 *
 * ★ 여기서 버튼을 누르면 **작업PC 의 수집기가 움직인다.** 사이트는 Workers 라서
 * 대표 PC 의 브라우저 창을 띄울 수 없다 — 그래서 수집기가 이 PC 안에서 작은 서버로
 * 떠 있고(`python -m collector serve`, 또는 `수집기 켜기.bat` 더블클릭), 이 화면이
 * 그것을 부른다. 사람이 터미널을 볼 일이 없게 하는 것이 이 화면의 목적이다.
 *
 * 두 곳을 함께 본다:
 *   · 사이트가 아는 것 — 마지막 수집 판정 (`/api/admin/crawl-status`)
 *   · 이 PC 가 아는 것 — 수집기가 켜져 있는가, 계정·세션이 준비됐는가 (127.0.0.1:8788)
 */

const POLL_MS = 2000;
export function Collect({ can }: { can: (p: string) => boolean }) {
  const [state, setState] = useState<AgentState | null>(null);
  const [agentDown, setAgentDown] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const site = useAsync(() => (can("admin.users") ? api.crawlStatus() : Promise.resolve(null)), []);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await agentGet());
      setAgentDown(false);
    } catch {
      // 수집기가 꺼져 있는 것은 오류가 아니다 — 아직 안 켠 것이다
      setAgentDown(true);
      setState(null);
    }
  }, []);

  useEffect(() => {
    void load();
    timer.current = window.setInterval(() => void load(), POLL_MS);
    return () => {
      if (timer.current !== null) window.clearInterval(timer.current);
    };
  }, [load]);

  const act = async (path: string, body?: unknown) => {
    setError(null);
    try {
      await agentPost(path, body);
      await load();
    } catch (err) {
      setError(err);
    }
  };

  const job = state?.job;
  const busy = job?.running === true;
  // 예전 수집기에는 자동 스위치가 없다. 없으면 "꺼짐" 으로 본다
  const daily = state?.daily ?? { on: false, next_at: null };

  return (
    <>
      <h1>수집</h1>
      <p className="page-desc">
        SECUI 에서 교체 시리얼을 받아 옵니다. 버튼은 <strong>이 PC 의 수집기</strong>를 움직입니다 —
        인증번호는 창에 직접 입력합니다.
      </p>

      <ErrorNotice error={error} />

      {agentDown ? (
        <div className="card">
          <h2>
            수집기가 꺼져 있습니다<span className="sub">이 PC 에서 한 번 켜 두시면 됩니다</span>
          </h2>
          <p>
            <code>apps/collector</code> 폴더의 <code>start-collector.bat</code> 을 더블클릭하세요.
            창 하나가 뜨고, <strong>그 창을 켜 둔 동안</strong> 이 화면의 버튼이 동작합니다.
          </p>
          {window.location.protocol === "https:" && (
            <p className="muted">
              배포된 사이트에서는 브라우저가 "로컬 네트워크 접근" 을 물을 수 있습니다 — <strong>허용</strong>을
              누르세요. 수집기 창이 배포 전에 켜진 것이면 닫고 다시 켜야 이 주소를 받아 줍니다. 폰에는
              수집기가 없으니 이 화면은 작업PC 에서만 씁니다.
            </p>
          )}
          <p className="muted">
            처음 한 번은 그 창이 필요한 것을 알아서 받습니다(2~3분). 받는 것은 그 폴더 안
            <code>.venv</code> 에만 들어가서, 이 PC 의 다른 파이썬은 건드리지 않습니다.
          </p>
        </div>
      ) : state === null ? (
        <Busy />
      ) : (
        <>
          <div className="card">
            <h2>
              준비 상태<span className="sub">셋이 다 초록이면 자동으로 돕니다</span>
            </h2>
            <div className="row" style={{ gap: 24, flexWrap: "wrap" }}>
              <Ready
                ok={state.keyring_ready === true}
                label="계정 저장소"
                hint="윈도우 자격 증명 관리자"
              />
              <Ready
                ok={typeof state.account === "string" && state.account.length > 0}
                label="SECUI 계정"
                hint={state.account ?? "아직 저장 안 됨"}
              />
              <Ready
                ok={state.session_saved === true}
                label="로그인 세션"
                hint={state.session_saved ? "살아 있음" : "로그인 필요"}
              />
              <Ready
                ok={state.api_key_set === true}
                label="사이트 키"
                hint={state.api_key_set ? "설정됨" : "설정 → API Key 에서 발급"}
              />
            </div>
          </div>

          <SetupCard state={state} onDone={act} />

          <div className="card">
            <h2>
              지금 하기<span className="sub">한 번에 하나만 돕니다</span>
            </h2>
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <button className="primary" disabled={busy} onClick={() => void act("/signin")}>
                {busy && job?.kind === "signin" ? "창이 떠 있습니다…" : "SECUI 로그인 창 열기"}
              </button>
              <button disabled={busy} onClick={() => void act("/crawl")}>
                지금 수집
              </button>
              <button disabled={busy} onClick={() => void act("/crawl?capture=1")}>
                수집 + 원문 저장
              </button>
              <div className="grow" />
              {daily.on ? (
                <button onClick={() => void act("/daily/off")}>자동 수집 끄기</button>
              ) : (
                <button onClick={() => void act("/daily/on")}>24시간마다 자동 수집 켜기</button>
              )}
            </div>
            <p className="muted" style={{ marginTop: 8 }}>
              {daily.on
                ? `자동 수집이 켜져 있습니다. 다음 ${formatKstStamp(daily.next_at) || "24시간 뒤"}. 세션이 끊기면 스스로 멈추고 알립니다.`
                : "자동 수집이 꺼져 있습니다. 켜 두면 하루 한 번 조용히 돕니다."}
            </p>
          </div>

          {job?.kind ? (
            <div className="card">
              <h2>
                진행<span className="sub">{job.running ? "돌고 있습니다" : "끝났습니다"}</span>
              </h2>
              {job.running ? (
                <Busy />
              ) : job.ok === false ? (
                <Notice kind="error">끝나지 못했습니다. 아래 기록을 봐 주세요.</Notice>
              ) : (
                <Notice kind="ok">끝났습니다.</Notice>
              )}
              {/* 수집기가 찍은 줄을 **그대로** 보여 준다. 요약하지 않는다 */}
              <pre className="mono" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                {(job.lines ?? []).join("\n") || "…"}
              </pre>
            </div>
          ) : null}
        </>
      )}

      {can("admin.users") && (
        <div className="card">
          <h2>
            사이트가 아는 마지막 수집<span className="sub">수집기가 사이트에 올린 기록</span>
          </h2>
          {site.loading ? (
            <Busy />
          ) : site.data === null || site.data === undefined ? null : (
            <SiteStatus status={site.data} />
          )}
        </div>
      )}
    </>
  );
}

/**
 * 준비하기 — 계정과 사이트 키를 **이 화면에서** 넣는다.
 *
 * 비밀번호는 이 PC 의 수집기로만 가고, 거기서 **윈도우 자격 증명 관리자**에 들어간다.
 * 사이트(D1)에도, 파일에도 적히지 않는다. 화면에도 다시 보이지 않는다.
 */
function SetupCard({
  state,
  onDone,
}: {
  state: AgentState;
  onDone: (path: string, body?: unknown) => Promise<void>;
}) {
  const [loginId, setLoginId] = useState(state.account ?? "");
  const [secret, setSecret] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);

  const accountReady = typeof state.account === "string" && state.account.length > 0;
  const keyReady = state.api_key_set === true;
  if (accountReady && keyReady) return null;

  const save = async (path: string, body: unknown, clear: () => void) => {
    setBusy(true);
    try {
      await onDone(path, body);
      clear();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h2>
        준비하기<span className="sub">한 번만 하시면 됩니다</span>
      </h2>

      {state.keyring_ready === false && (
        <Notice kind="warn">
          이 PC 에 계정 저장소가 아직 준비되지 않았습니다. <code>start-collector.bat</code> 창을
          닫았다가 다시 열면 필요한 것을 알아서 받습니다.
        </Notice>
      )}

      {!accountReady && (
        <div style={{ marginTop: 12 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label className="field">
              SECUI ID (이메일)
              <input value={loginId} onChange={(e) => setLoginId(e.target.value)} />
            </label>
            <label className="field">
              비밀번호
              <input
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="이 PC 안에만 저장됩니다"
              />
            </label>
            <button
              className="primary"
              disabled={busy || loginId.trim() === "" || secret === ""}
              onClick={() =>
                void save("/account", { login_id: loginId.trim(), secret }, () => setSecret(""))
              }
            >
              계정 저장
            </button>
          </div>
          <p className="muted" style={{ marginTop: 4 }}>
            비밀번호는 <strong>이 PC 의 윈도우 자격 증명 관리자</strong>에 들어갑니다 — 사이트에도
            파일에도 적히지 않습니다. 인증번호는 저장하지 않습니다(그건 매번 직접 치십니다).
          </p>
        </div>
      )}

      {!keyReady && (
        <div style={{ marginTop: 16 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label className="field grow">
              사이트 키 (설정 → API Key → ingest 키 발급 후 붙여넣기)
              <input
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="orbit_..."
              />
            </label>
            <button
              disabled={busy || apiKey.trim() === ""}
              onClick={() => void save("/apikey", { api_key: apiKey.trim() }, () => setApiKey(""))}
            >
              키 저장
            </button>
          </div>
          <p className="muted" style={{ marginTop: 4 }}>
            이 키가 있어야 수집 결과가 사이트에 올라옵니다. 폐기하면 그 순간 무효가 됩니다.
          </p>
        </div>
      )}
    </div>
  );
}

function Ready({ ok, label, hint }: { ok: boolean; label: string; hint: string }) {
  return (
    <div>
      <div className="muted">{label}</div>
      <div style={{ color: ok ? "var(--ok)" : "var(--danger)", fontWeight: 600 }}>
        {ok ? "준비됨" : "필요함"}
      </div>
      <div className="muted" style={{ fontSize: 12 }}>
        {hint}
      </div>
    </div>
  );
}

function SiteStatus({ status }: { status: CrawlStatus }) {
  if (status.never_ran) {
    return <Notice kind="warn">아직 한 번도 올라온 기록이 없습니다.</Notice>;
  }
  return (
    <>
      {status.needs_login ? (
        <Notice kind="error">
          <strong>세션이 끊겼습니다.</strong> 위에서 [SECUI 로그인 창 열기] 를 눌러 주세요.
        </Notice>
      ) : status.stale ? (
        <Notice kind="warn">마지막 성공이 26시간을 넘었습니다.</Notice>
      ) : (
        <Notice kind="ok">정상입니다. 마지막 성공 {formatKstStamp(status.last_ok_at)}.</Notice>
      )}
      {status.last_run && (
        <div className="muted" style={{ marginTop: 8 }}>
          마지막 실행 {formatKstStamp(status.last_run.started_at, true)} ·{" "}
          {status.last_run.verdict ?? "—"} · 본 건 {status.last_run.cases_found ?? "—"} · 교체 연결{" "}
          {status.last_run.swaps_matched ?? "—"}
          {status.last_run.error && ` · ${status.last_run.error}`}
        </div>
      )}
    </>
  );
}
